import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper
} from '@mui/material';
import MainCard from '../../components/MainCard';
import Sidebar from "../../menu-items/Sidebar";
import axios from 'axios';
import Cookies from 'js-cookie';

const normalizeUsers = (payload) => {
  // Accept a variety of common shapes and always return an array
  if (Array.isArray(payload)) return payload;
  if (payload == null || typeof payload !== 'object') return [];
  return (
    payload.data ||
    payload.results ||
    payload.items ||
    payload.users ||
    []
  );
};

const getAuthToken = () => {
  // Prefer axios default if set, else cookie named "token" (adjust if your cookie key differs)
  const fromAxios = axios.defaults?.headers?.common?.Authorization;
  if (fromAxios) return fromAxios;
  const cookieToken = Cookies.get('token');
  return cookieToken ? `Bearer ${cookieToken}` : undefined;
};

const fmtDate = (val) => {
  if (!val) return '-';
  // Try to parse; fallback to the raw string
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  // Escape double quotes by doubling them and wrap in quotes to handle commas/newlines
  return `"${s.replace(/"/g, '""')}"`;
};

const UserList = () => {
  const [data, setData] = useState([]); // always an array
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // align with options below
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErrMsg('');
      try {
        const token = getAuthToken();
        const url = `${process.env.REACT_APP_BASE_URL}admin/reports/active_user_list`;

        const config = token
          ? { headers: { Authorization: token } }
          : undefined;

        const response = await axios.get(url, config);
        // Console log in case you need to inspect shape
        // console.log('API Response:', response.data);

        const users = normalizeUsers(response?.data);
        setData(Array.isArray(users) ? users : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrMsg(error?.response?.data?.message || error?.message || 'Failed to fetch');
        setData([]); // ensure array
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadAsCSV = () => {
    const headers = ['#', 'Email', 'Practice', 'Created At'];
    const csvRows = [headers.map(csvEscape).join(',')];

    // Export ALL rows (not just current page)
    data.forEach((row, index) => {
      const rowData = [
        index + 1,
        row?.email ?? '',
        row?.practice ?? '',
        fmtDate(row?.created_at)
      ];
      csvRows.push(rowData.map(csvEscape).join(','));
    });

    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'user_list.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const displayedData = useMemo(() => {
    // Defensive: data is always an array; slice is safe
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return data.slice(start, end);
  }, [data, page, rowsPerPage]);

  return (
    <Box sx={{ border: '1px solid #ccc', padding: 5, minHeight: '100vh' }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.75}>
        <Grid item xs={12} md={3} lg={2}>
          <Sidebar />
        </Grid>

        <Grid item xs={12} md={9} lg={10}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 'bold', fontSize: '2rem' }}
            >
              User List
            </Typography>

            <Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ ml: 2 }}
                onClick={downloadAsCSV}
                disabled={loading || !data.length}
              >
                Download as CSV
              </Button>
            </Box>
          </Box>

          <MainCard title="">
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '15px',
                boxShadow: 3,
                overflow: 'hidden',
                mt: 2
              }}
            >
              <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Practice</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Created At</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : errMsg ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {errMsg}
                      </TableCell>
                    </TableRow>
                  ) : displayedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedData.map((row, index) => (
                      <TableRow
                        key={row?.id || row?.email || index}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            cursor: 'pointer',
                          },
                          borderBottom: '1px solid #ddd',
                        }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{row?.email ?? '-'}</TableCell>
                        <TableCell>{row?.practice ?? '-'}</TableCell>
                        <TableCell>{fmtDate(row?.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </MainCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserList;
